import {
    Box,
    Typography,
    useTheme,
    CircularProgress
} from '@mui/material';
import React, { useEffect } from 'react';
import { PieChart, pieArcLabelClasses } from '@mui/x-charts/PieChart';
import PortalCard from '../../../../../components/PortalCard';
import analyticsService from '../../../../../services/analytics/AnalyticsService';

const StockLevelDistribution: React.FC = () => {
    const theme = useTheme();
    const [loading, setLoading] = React.useState(false);
    const [data, setData] = React.useState<Array<{
        stockLevel: string;
        productCount: number;
        totalStock: number;
    }>>([]);
    const [error, setError] = React.useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await analyticsService.getStockLevelDistribution();
                if (result.success && result.data) {
                    setData(result.data);
                } else {
                    setError(result.message || 'Failed to fetch stock level distribution');
                }
            } catch (err: any) {
                setError(err.message || 'Failed to fetch stock level distribution');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const pieData = data.map((item, index) => ({
        id: index,
        value: item.productCount,
        label: item.stockLevel,
    }));

    const colors = [theme.palette.error.main, theme.palette.warning.main, theme.palette.success.main];
    const totalProducts = data.reduce((sum, item) => sum + item.productCount, 0);

    return (
        <PortalCard
            sx={{
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Box sx={{ mb: 2 }}>
                <Typography variant='h6' fontFamily='primaryFont' fontWeight='bold' sx={{ mb: 0.5 }}>
                    Stock Level Distribution
                </Typography>
                <Typography variant='body2' color='text.secondary' fontFamily='primaryFont'>
                    Products by stock level
                </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', minHeight: 300 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant='body2' color='error' fontFamily='primaryFont'>
                            {error}
                        </Typography>
                    </Box>
                ) : data.length > 0 ? (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3 }}>
                            <PieChart
                                series={[{
                                    data: pieData,
                                    innerRadius: 40,
                                    outerRadius: 100,
                                    arcLabel: (item) => {
                                        const percentage = totalProducts > 0 ? Math.round((item.value / totalProducts) * 100) : 0;
                                        return `${percentage}%`;
                                    },
                                    arcLabelMinAngle: 30,
                                    paddingAngle: 2,
                                    cornerRadius: 4,
                                }]}
                                width={300}
                                height={250}
                                colors={colors}
                                sx={{
                                    [`& .${pieArcLabelClasses.root}`]: {
                                        fill: theme.palette.text.primary,
                                        fontWeight: 'bold',
                                        fontSize: '0.875rem',
                                        fontFamily: 'primaryFont',
                                    },
                                }}
                                slotProps={{
                                    legend: { 
                                        hidden: true
                                    }
                                }}
                            />
                        </Box>
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 3, 
                            justifyContent: 'center', 
                            alignItems: 'flex-start',
                            flexWrap: 'wrap',
                            width: '100%',
                            px: 2
                        }}>
                            {data.map((item, index) => {
                                const percentage = totalProducts > 0 ? Math.round((item.productCount / totalProducts) * 100) : 0;
                                return (
                                    <Box 
                                        key={item.stockLevel} 
                                        sx={{ 
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            minWidth: 120,
                                            flex: '1 1 auto'
                                        }}
                                    >
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 1, 
                                            mb: 1,
                                            justifyContent: 'center',
                                            width: '100%'
                                        }}>
                                            <Box
                                                sx={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: '50%',
                                                    backgroundColor: colors[index],
                                                    flexShrink: 0
                                                }}
                                            />
                                            <Typography 
                                                variant='body2' 
                                                fontFamily='primaryFont' 
                                                fontWeight={600}
                                                sx={{ textTransform: 'capitalize' }}
                                            >
                                                {item.stockLevel}
                                            </Typography>
                                        </Box>
                                        <Typography 
                                            variant='h5' 
                                            fontFamily='primaryFont' 
                                            fontWeight='bold'
                                            sx={{ mb: 0.5, lineHeight: 1.2 }}
                                        >
                                            {item.productCount}
                                        </Typography>
                                        <Typography 
                                            variant='caption' 
                                            color='text.secondary' 
                                            fontFamily='primaryFont'
                                            sx={{ textAlign: 'center', lineHeight: 1.4 }}
                                        >
                                            {percentage}% • {item.totalStock.toLocaleString()} units
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant='body2' color='text.secondary' fontFamily='primaryFont'>
                            No data available
                        </Typography>
                    </Box>
                )}
            </Box>
        </PortalCard>
    );
};

export default StockLevelDistribution;

